import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { lastValueFrom, Subscription } from 'rxjs';
import { ListStateService } from '../services/list-state/list-state.service';
import moment from "moment";
import * as XLSX from 'xlsx';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';

import { InventoryService, InventoryFilters } from '../services/inventory/inventory.service';
import { SubsidiaryService } from '../services/subsidiary/subsidiary.service';
import { ToasterService } from '@ui-nuvertos/auth';
import {
  DynamicListingComponent,
  FilterConfig,
  ColumnConfig,
  ActionConfig,
  ActionEvent,
  FilterEvent,
} from '../dynamic-listing/dynamic-listing';
import { SVG_ICONS } from '../utils/svg-icons.utils';
import { BulkUploadComponent } from '../bulk-upload/bulk-upload';

@Component({
  selector: 'lib-inventory-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DynamicListingComponent,
    BulkUploadComponent,
    ButtonModule,
    CheckboxModule,
    TooltipModule,
    SelectModule,
    MultiSelectModule,
    DatePickerModule,
    DialogModule
  ],
  templateUrl: './inventory-list.html',
  styleUrls: ['./inventory-list.css'],
})
export class InventoryList implements OnInit, OnDestroy {
  private readonly listStateService = inject(ListStateService);
  private pageSubscription?: Subscription;
  private initialPageLoaded = false;

  isLoading = false;
  exportFilename = `inventory_${new Date().toISOString().slice(0, 10)}`;
  showBulkUploadDialog = false;

  headerActionConfigs: ActionConfig[] = [
    {
      key: 'stock-report',
      label: 'Stock Report',
      tooltip: 'Download inventory stock report',
      severity: 'secondary',
      icon: 'pi pi-file-excel',
    },
    {
      key: 'bulk-upload',
      label: 'Bulk Upload',
      tooltip: 'Upload inventory from CSV/Excel',
      severity: 'secondary',
      icon: 'pi pi-cloud-upload',
    },
  ];

  tableData: any[] = [];
  filterValues: { [key: string]: any } = {};

  filterConfigs: FilterConfig[] = [];

  columnConfigs: ColumnConfig[] = [
    { key: 'item_number', header: 'Item No.' },
    { key: 'item_name', header: 'Item Name', ellipsis: true },
    { key: 'description', header: 'Description', ellipsis: true },
    { key: 'location', header: 'Location' },
    { key: 'expiry_date', header: 'Expiry Date' },
    { key: 'salt', header: 'Salt', ellipsis: true },
    { key: 'type', header: 'Type' },
    { key: 'unit', header: 'Unit' },
    { key: 'packing_unit', header: 'Packing Unit' },
    { key: 'sku', header: 'SKU' },
    { key: 'manufacturer', header: 'Manufacturer', ellipsis: true },
    { key: 'current_stock', header: 'Current Stock', type: 'number', align: 'right' },
    { key: 'minimum_stock', header: 'Minimum Stock Level', type: 'number', align: 'right' },
    { key: 'maximum_stock', header: 'Maximum Stock Level', type: 'number', align: 'right' },
    { key: 'rack_no', header: 'Rack No.' },
  ];

  actionConfigs: ActionConfig[] = [
    { key: 'view', label: 'View', tooltip: 'View inventory ledger', severity: 'secondary', svgIcon: SVG_ICONS.VIEW },
  ];

  pageIndex = 1;
  rowsPerPage = 10;
  totalCount = 0;
  totalPages = 0;

  // Summary totals
  totalMRP = 0;
  totalRate = 0;

  // Filter checkboxes
  showLowStock = false;
  showNearExpiry = false;

  // Location selector
  locationOptions: { id: string; value: string; label: string; raw?: any }[] = [];
  selectedLocationId: string | null = null;
  locationsLoaded = false;

  // Stock Report Export
  showStockReportDialog = false;
  subsidiaryOptions: { label: string; value: any; isSelectAll?: boolean }[] = [];
  selectedSubsidiaries: any[] = [];
  selectedMonth: Date | null = null;
  isExportingReport = false;
  isExportingCurrentStock = false;
  // Bumped every time the dialog is opened, so an export that resolves late can
  // tell whether the dialog it was started from is still the one on screen.
  private stockReportDialogGeneration = 0;
  stockReportMode: 'monthly' | 'date_range' = 'monthly';
  stockReportStartDate: Date | null = null;
  stockReportEndDate: Date | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private inventoryService: InventoryService,
    private subsidiaryService: SubsidiaryService,
    private toasterService: ToasterService
  ) {}

  ngOnInit() {
    this.initializeFilterConfigs();
    // Subscribe to URL page changes
    this.pageSubscription = this.listStateService.getPageFromRoute(this.route).subscribe(page => {
      // Only load if locations are already loaded, otherwise loadLocations will trigger it
      if (this.locationsLoaded) {
        this.loadInventories(page);
      } else {
        // Store the page to load after locations are ready
        this.pageIndex = page;
      }
    });
    this.loadLocations();
  }

  ngOnDestroy() {
    this.pageSubscription?.unsubscribe();
  }

  async loadLocations() {
    try {
      const response = await lastValueFrom(this.subsidiaryService.getSubsidiaries());
      const subsidiaries = response?.data?.subsidiaries || response?.data || [];
      
      // Extract all location addresses from all subsidiaries
      // Populate subsidiary options for stock report export
      this.subsidiaryOptions = [
        { label: 'Select All', value: 'SELECT_ALL_VALUE', isSelectAll: true },
        ...(Array.isArray(subsidiaries) ? subsidiaries : []).map((sub: any) => ({
          label: sub.name || sub.subsidiary_name || 'Unknown',
          value: sub.id,
        }))
      ];

      const allLocations: { id: string; value: string; label: string; raw?: any }[] = [];
      (Array.isArray(subsidiaries) ? subsidiaries : []).forEach((subsidiary: any) => {
        const addresses = subsidiary?.tax_information || [];
        (Array.isArray(addresses) ? addresses : []).forEach((addr: any) => {
          if (addr?.id && !addr?.is_deleted) {
            allLocations.push({
              id: String(addr.id),
              value: String(addr.id),
              label: `${subsidiary.name || 'Subsidiary'} - ${addr.address || addr.gst_number || 'Location'}`,
              raw: { ...addr, subsidiary_name: subsidiary.name }
            });
          }
        });
      });

      this.locationOptions = [
        { id: 'all', value: 'all', label: 'All Locations' },
        ...allLocations
      ];
      this.locationsLoaded = true;

      // Auto-select first location and load inventories with the page from URL
      if (this.locationOptions.length > 0) {
        this.selectedLocationId = this.locationOptions[0].value;
        // Use pageIndex which may have been set from URL subscription
        await this.loadInventories(this.pageIndex);
      } else {
        this.toasterService.warn('Warning', 'No locations available. Please set up subsidiaries with locations.');
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      this.toasterService.error('Error', 'Failed to load locations');
    }
  }

  async onLocationChange(event: any) {
    this.selectedLocationId = event?.value || event;
    this.pageIndex = 1; // Reset to first page
    await this.loadInventories();
  }

  initializeFilterConfigs() {
    this.filterConfigs = [
      {
        key: 'item_number',
        type: 'text',
        placeholder: 'Item No.',
        value: this.filterValues['item_number'],
        showInAccordion: true,
      },
      {
        key: 'item_name',
        type: 'text',
        placeholder: 'Item Name',
        value: this.filterValues['item_name'],
        showInAccordion: true,
      },
      {
        key: 'location',
        type: 'text',
        placeholder: 'Location',
        value: this.filterValues['location'],
        showInAccordion: true,
      },
      {
        key: 'unit',
        type: 'text',
        placeholder: 'Unit',
        value: this.filterValues['unit'],
        showInAccordion: true,
      },
      {
        key: 'manufacturer',
        type: 'text',
        placeholder: 'Manufacturer',
        value: this.filterValues['manufacturer'],
        showInAccordion: true,
      },
    ];
  }

  async loadInventories(page: number = 1) {
    // Require location to be selected
    if (!this.selectedLocationId) {
      this.tableData = [];
      this.totalCount = 0;
      this.totalPages = 0;
      return; // Don't search without location
    }

    try {
      this.isLoading = true;
      this.pageIndex = page;

      const filters: InventoryFilters = {
        limit: this.rowsPerPage,
        offset: (this.pageIndex - 1) * this.rowsPerPage,
        is_deleted: false,
        positive_only: false,
      };

      if (this.selectedLocationId === 'all') {
        filters.all_location = true;
      } else {
        filters.location_id = this.selectedLocationId;
      }

      // Apply text filters
      if (this.filterValues['item_number']) {
        filters.serial_number = this.filterValues['item_number'];
      }
      
      if (this.filterValues['item_name']) {
        filters.item_name = this.filterValues['item_name'];
      }

      // Apply checkbox filters
      if (this.showLowStock) {
        // This would need backend support - for now we'll filter client-side
      }

      if (this.showNearExpiry) {
        // This would need backend support - for now we'll filter client-side
      }

      const response = await lastValueFrom(this.inventoryService.searchInventories(filters));
      const inventories = response?.data?.inventories ?? [];

      // Transform inventory data to match table structure
      this.tableData = inventories.map((inventory: any, index: number) => {
        // Calculate totals
        const mrp = inventory.mrp || 0;
        const rate = inventory.rate || 0;
        const quantity = inventory.current_quantity || 0;
        
        this.totalMRP += mrp * quantity;
        this.totalRate += rate * quantity;

        return {
          id: inventory.id || '',
          item_number: inventory.serial_number || inventory.item_id || '',
          item_name: inventory.item_master_name || '',
          description: inventory.item_master_description || '',
          location: inventory.location_name || inventory.location || 'N/A',
          expiry_date: inventory.expiration_date ? moment(inventory.expiration_date).format('DD-MM-YYYY') : 'N/A',
          salt: inventory.composition || 'N/A',
          type: inventory.item_type_name || 'N/A',
          unit: inventory.packaging_name || 'N/A',
          packing_unit: inventory.packing_unit || inventory.pack || 'N/A',
          sku: inventory.sku || 'N/A',
          manufacturer: inventory.manufacturer_name || 'N/A',
          current_stock: inventory.current_quantity || 0,
          minimum_stock: inventory.minimum_quantity || 'N/A',
          maximum_stock: inventory.maximum_quantity || 'N/A',
          rack_no: inventory.rack_number || 'N/A',
          mrp: mrp,
          rate: rate,
        };
      });

      // Reset totals before calculating
      this.totalMRP = 0;
      this.totalRate = 0;
      
      // Recalculate totals
      inventories.forEach((inventory: any) => {
        const mrp = inventory.mrp || 0;
        const rate = inventory.rate || 0;
        const quantity = inventory.current_quantity || 0;
        this.totalMRP += mrp * quantity;
        this.totalRate += rate * quantity;
      });

      this.totalCount = response?.data?.total ?? this.tableData.length;
      this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.rowsPerPage));
    } catch (error) {
      console.error('Error loading inventories:', error);
      this.tableData = [];
      this.totalCount = 0;
      this.totalPages = 0;
      this.totalMRP = 0;
      this.totalRate = 0;
      this.toasterService.error('Error', 'Failed to load inventory items');
    } finally {
      this.isLoading = false;
    }
  }

  onFilterEvent(event: FilterEvent) {
    this.filterValues = event.data ?? {};
    // Reset to page 1 when filters change
    this.listStateService.clearPage();
    // Always reload when filters change (URL subscription may not trigger if already on page 1)
    this.loadInventories(1);
  }

  onColumnsSelected(_: string[]) {
    // Future: save column preferences
  }

  onActionClicked(event: ActionEvent) {
    if (event.action === 'bulk-upload') {
      this.showBulkUploadDialog = true;
      return;
    }

    if (event.action === 'stock-report') {
      this.stockReportDialogGeneration++;
      this.showStockReportDialog = true;
      return;
    }

    const inventoryId = event.row?.id;
    if (!inventoryId) return;

    switch (event.action) {
      case 'view':
        this.view(inventoryId);
        break;
    }
  }

  onBulkUploadSuccess(_event: any) {
    this.showBulkUploadDialog = false;
    this.loadInventories(this.pageIndex);
  }

  onPageChanged(page: number) {
    // Update URL - this triggers the subscription which loads the data
    this.listStateService.setPage(page);
  }

  onShowLowStockChange(event: any) {
    this.showLowStock = event.checked;
    this.pageIndex = 1;
    this.loadInventories(this.pageIndex);
  }

  onShowNearExpiryChange(event: any) {
    this.showNearExpiry = event.checked;
    this.pageIndex = 1;
    this.loadInventories(this.pageIndex);
  }

  // Export functions
  exportCsv() {
    if (!this.tableData.length) {
      this.toasterService.info('Info', 'No data available to export.');
      return;
    }

    const headers = [
      'S.No.', 'Item No.', 'Item Name', 'Description', 'Location', 'Expiry Date', 'Salt', 'Type', 
      'Unit', 'Packing Unit', 'SKU', 'Manufacturer', 'Current Stock', 
      'Minimum Stock Level', 'Maximum Stock Level', 'Rack No.'
    ] as const;

    type ExportRow = { [K in typeof headers[number]]: string | number };

    const rows: ExportRow[] = this.tableData.map((row, index) => ({
      'S.No.': (this.pageIndex - 1) * this.rowsPerPage + index + 1,
      'Item No.': row.item_number ?? '',
      'Item Name': row.item_name ?? '',
      'Description': row.description ?? '',
      'Location': row.location ?? '',
      'Expiry Date': row.expiry_date ?? 'N/A',
      'Salt': row.salt ?? '',
      'Type': row.type ?? '',
      'Unit': row.unit ?? '',
      'Packing Unit': row.packing_unit ?? '',
      'SKU': row.sku ?? '',
      'Manufacturer': row.manufacturer ?? '',
      'Current Stock': row.current_stock ?? 0,
      'Minimum Stock Level': row.minimum_stock ?? 'N/A',
      'Maximum Stock Level': row.maximum_stock ?? 'N/A',
      'Rack No.': row.rack_no ?? 'N/A',
    }));

    const csvContent = [
      headers.join(','),
      ...rows.map(row =>
        headers
          .map(header => {
            const cell = row[header] ?? '';
            const escaped = cell.toString().replace(/"/g, '""');
            return `"${escaped}"`;
          })
          .join(',')
      ),
    ].join('\n');

    // BOM for Excel UTF-8 support
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${this.exportFilename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  exportXlsx() {
    if (!this.tableData.length) {
      this.toasterService.info('Info', 'No data available to export.');
      return;
    }

    const headers = [
      'S.No.', 'Item No.', 'Item Name', 'Description', 'Location', 'Expiry Date', 'Salt', 'Type', 
      'Unit', 'Packing Unit', 'SKU', 'Manufacturer', 'Current Stock', 
      'Minimum Stock Level', 'Maximum Stock Level', 'Rack No.'
    ] as const;

    const data = this.tableData.map((row, index) => ({
      'S.No.': (this.pageIndex - 1) * this.rowsPerPage + index + 1,
      'Item No.': row.item_number ?? '',
      'Item Name': row.item_name ?? '',
      'Description': row.description ?? '',
      'Location': row.location ?? '',
      'Expiry Date': row.expiry_date ?? 'N/A',
      'Salt': row.salt ?? '',
      'Type': row.type ?? '',
      'Unit': row.unit ?? '',
      'Packing Unit': row.packing_unit ?? '',
      'SKU': row.sku ?? '',
      'Manufacturer': row.manufacturer ?? '',
      'Current Stock': row.current_stock ?? 0,
      'Minimum Stock Level': row.minimum_stock ?? 'N/A',
      'Maximum Stock Level': row.maximum_stock ?? 'N/A',
      'Rack No.': row.rack_no ?? 'N/A',
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    worksheet['!cols'] = headers.map(() => ({ wch: 20 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Inventory');
    XLSX.writeFile(workbook, `${this.exportFilename}.xlsx`);
  }

  exportPdf() {
    window.print();
  }

  view(inventoryId: string) {
    // Navigate to inventory ledger page
    this.router.navigate(['/inventory', inventoryId, 'ledger']);
  }

  onSubsidiarySelectionChange(event: any): void {
    const changedItem = event.itemValue;
    if (changedItem?.value === 'SELECT_ALL_VALUE') {
      const hasSelectAll = this.selectedSubsidiaries.some(
        (s: any) => s.value === 'SELECT_ALL_VALUE'
      );
      if (hasSelectAll) {
        this.selectedSubsidiaries = [...this.subsidiaryOptions];
      } else {
        this.selectedSubsidiaries = [];
      }
    } else {
      const nonSelectAll = this.subsidiaryOptions.filter(o => o.value !== 'SELECT_ALL_VALUE');
      const selectedNonSelectAll = this.selectedSubsidiaries.filter((s: any) => s.value !== 'SELECT_ALL_VALUE');
      if (selectedNonSelectAll.length === nonSelectAll.length) {
        this.selectedSubsidiaries = [...this.subsidiaryOptions];
      } else {
        this.selectedSubsidiaries = this.selectedSubsidiaries.filter((s: any) => s.value !== 'SELECT_ALL_VALUE');
      }
    }
  }

  private getSubsidiaryIdsForApi(): number[] {
    return this.selectedSubsidiaries
      .filter((sub: any) => sub.value !== 'SELECT_ALL_VALUE')
      .map((sub: any) => sub.value);
  }

  async downloadStockReport(): Promise<void> {
    if (!this.selectedSubsidiaries.length) {
      this.toasterService.warn('Warning', 'Please select subsidiaries');
      return;
    }

    if (this.stockReportMode === 'monthly' && !this.selectedMonth) {
      this.toasterService.warn('Warning', 'Please select a month');
      return;
    }

    if (this.stockReportMode === 'date_range' && (!this.stockReportStartDate || !this.stockReportEndDate)) {
      this.toasterService.warn('Warning', 'Please select start date and end date');
      return;
    }

    if (this.stockReportMode === 'date_range' && this.stockReportStartDate && this.stockReportEndDate && this.stockReportStartDate > this.stockReportEndDate) {
      this.toasterService.warn('Warning', 'Start date must be before end date');
      return;
    }

    try {
      this.isExportingReport = true;
      const subsidiaryIds = this.getSubsidiaryIdsForApi();
      let blob: Blob;
      let fileName: string;

      if (this.stockReportMode === 'date_range') {
        const startDate = this.formatDateForApi(this.stockReportStartDate!);
        const endDate = this.formatDateForApi(this.stockReportEndDate!);
        blob = await lastValueFrom(this.inventoryService.exportStockReportByDateRange(subsidiaryIds, startDate, endDate));
        fileName = `inventory-stock-report-${startDate}-to-${endDate}.xlsx`;
      } else {
        const month = `${this.selectedMonth!.getFullYear()}-${String(this.selectedMonth!.getMonth() + 1).padStart(2, '0')}`;
        blob = await lastValueFrom(this.inventoryService.exportStockReport(subsidiaryIds, month));
        fileName = `inventory-stock-report-${month}.xlsx`;
      }

      this.saveBlobAsFile(blob, fileName);

      this.showStockReportDialog = false;
      this.toasterService.success('Success', 'Stock report downloaded');
    } catch (error) {
      console.error('Error downloading stock report:', error);
      this.toasterService.error('Error', 'Failed to download stock report');
    } finally {
      this.isExportingReport = false;
    }
  }

  /**
   * Downloads a dump of inventory as it stands right now.
   * Deliberately independent of stockReportMode — current stock needs no dates.
   */
  async downloadCurrentStock(): Promise<void> {
    // Guard on the ids actually sent, not on selectedSubsidiaries: that array
    // also holds the synthetic "Select All" entry, which getSubsidiaryIdsForApi
    // strips — so a non-empty selection can still yield zero ids.
    const subsidiaryIds = this.getSubsidiaryIdsForApi();
    if (!subsidiaryIds.length) {
      this.toasterService.warn('Warning', 'Please select subsidiaries');
      return;
    }

    try {
      this.isExportingCurrentStock = true;
      const startedAtGeneration = this.stockReportDialogGeneration;
      const response = await lastValueFrom(this.inventoryService.exportCurrentStockReport(subsidiaryIds));
      const fallback = `inventory-stock-report-current-${this.formatDateForApi(new Date())}.xlsx`;

      this.saveBlobAsFile(response.body!, this.fileNameFromResponse(response) ?? fallback);

      this.closeStockReportDialogIfReopened(startedAtGeneration);
      this.toasterService.success('Success', 'Current inventory downloaded');
    } catch (error) {
      console.error('Error downloading current inventory:', error);
      this.toasterService.error('Error', 'Failed to download current inventory');
    } finally {
      this.isExportingCurrentStock = false;
    }
  }

  /** Prefers the server's filename so the as-of date matches the sheet's title. */
  private fileNameFromResponse(response: HttpResponse<Blob>): string | null {
    const header = response.headers.get('Content-Disposition');
    if (!header) return null;
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
    return match ? decodeURIComponent(match[1].trim()) : null;
  }

  /**
   * Closes the dialog only if it is still the one this export was started from.
   * Without this a slow export force-closes a dialog the user closed and
   * reopened meanwhile, discarding whatever they had started entering.
   */
  private closeStockReportDialogIfReopened(startedAtGeneration: number): void {
    if (this.stockReportDialogGeneration !== startedAtGeneration) return;
    this.showStockReportDialog = false;
  }

  private saveBlobAsFile(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private formatDateForApi(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  get isStockReportDownloadDisabled(): boolean {
    if (!this.selectedSubsidiaries.length) return true;
    if (this.stockReportMode === 'monthly') return !this.selectedMonth;
    return !this.stockReportStartDate || !this.stockReportEndDate;
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(value);
  }
}

